import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionNcrService } from '../services/ncr.service';
import type { ConstructionNCR, NcrFilters } from '../types/construction-ncr.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionNcrs(companyId: string, initialFilters: NcrFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<NcrFilters>({
    status: 'all',
    severity: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-ncrs', companyId, filters],
    queryFn: () => ConstructionNcrService.getNcrs(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-ncr-stats', companyId, filters.projectId],
    queryFn: () => ConstructionNcrService.getNcrStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionNCR>) => ConstructionNcrService.createNcr(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-ncrs'] });
      toast.success('Khởi tạo báo cáo NCR thành công');
    },
  });

  return {
    ncrs: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createNcr: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useConstructionNcrDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-ncrs', 'detail', id],
    queryFn: () => ConstructionNcrService.getNcrDetail(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ConstructionNCR>) => ConstructionNcrService.updateNcr(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-ncrs'] });
      queryClient.invalidateQueries({ queryKey: ['construction-ncrs', 'detail', id] });
      toast.success('Cập nhật NCR thành công');
    },
  });

  return {
    ncr: query.data,
    isLoading: query.isLoading,
    updateNcr: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
